import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";

// BİLDİRİMLER — the staff notification feed (secondary-surfaces §2, backend #236).
// The console hook layer over the two READY routes:
//   • GET  /v1/notifications       → the COMPOSED, scope-aware, read-decorated feed
//                                     (a single-page Page envelope; server-sorted
//                                     critical-first then newest-first).
//   • POST /v1/notifications/read  → flip key(s) read/unread (idempotent server-side).
// Follows the console read pattern (useQuery/useMutation + useApi). NO marketing —
// the backend emits operational signals only; the console adds none.

/** The six operational signal sources (validation.NOTIFICATION_SOURCE_TYPES). */
export type NotificationType =
  | "APPROVAL"
  | "SLA"
  | "PAID_NOT_ISSUED"
  | "CUSTOMER_RESPONSE"
  | "HOUSEHOLD_ASSIGNED"
  | "RENEWAL_FORCED_HIGH";

/** Genuine-alarm vs calm — critical is red-only (PAID_NOT_ISSUED, SLA breach). */
export type NotificationSeverity = "critical" | "normal";

/** The deep-link target kind — mapped to an existing console route by the surface. */
export type NotificationRefKind =
  | "approval"
  | "queue_item"
  | "policy_application"
  | "recommendation"
  | "household"
  | "renewal_schedule";

/** One feed item — the exact backend shape (notification.service `NotificationItem`).
 *  `key` = `${TYPE}:${ref.id}`, the deterministic mark key. `occurredAt` is ISO over
 *  the wire. `title` is an operational label (server TR); the surface renders its OWN
 *  i18n type label instead, so the feed reads cleanly in both locales. */
export interface NotificationItem {
  key: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  ref: { kind: NotificationRefKind; id: string };
  occurredAt: string;
  read: boolean;
}

/** The frozen list envelope (@csa/types Page) — the feed is a single page. */
interface NotificationPage {
  data: NotificationItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

const FEED_KEY = ["notifications", "feed"];

/** GET /v1/notifications — the composed, scope-aware, read-decorated feed. No retry:
 *  an empty feed is a state the surface renders calmly, not a transient to hammer. */
export function useNotifications() {
  const api = useApi();
  return useQuery({
    queryKey: FEED_KEY,
    retry: false,
    queryFn: () => api.get<NotificationPage>("/v1/notifications"),
  });
}

/** POST /v1/notifications/read — set the read-state of key(s) (idempotent). Refetches
 *  the feed on settle so the state re-decorates from the server (the source of truth).
 *
 *  GAP-AUDIT-3 D15 — `read` used to be hard-coded `true`. The endpoint accepts
 *  `read: false` and the FE never sent it, so marking something read was a ONE-WAY
 *  door: an advisor who cleared an item they had not actually dealt with could not
 *  put it back. The parameter is now honoured — callers pass `{ keys, read }`, and
 *  `read` defaults to true so the common "mark read" case stays a one-field call. */
export function useMarkNotificationsRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keys, read = true }: { keys: string[]; read?: boolean }) =>
      api.post<{ read: boolean; count: number }>("/v1/notifications/read", { keys, read }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: FEED_KEY });
      // A8 — the shell badge is a SEPARATE query, so it must be invalidated too.
      // Without this the count would keep claiming unread items the advisor has
      // just cleared, which is precisely the distrust the badge exists to avoid.
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
}

const UNREAD_KEY = ["notifications", "unread-count"];

/**
 * A8 · GET /v1/notifications/unread-count — the shell bell badge.
 *
 * Separate from the feed on purpose: the bell renders on EVERY page, and making
 * the whole shell fetch and hold the entire feed to show one integer would be a
 * poor trade. The server derives the count from the same composition as the
 * feed, so the two can never disagree.
 *
 * No retry and no polling. A stale-by-a-minute count is a calm inaccuracy; a
 * shell that re-hammers a failing endpoint from every page is not. It refreshes
 * when the feed is marked (above) and on normal remount.
 *
 * `enabled` is the caller's queue:read grant. The endpoint is gated on it, so
 * for a principal without it this read is a KNOWN 403 — and the shell renders on
 * every page, which would make it a 403 per navigation, in the logs, forever.
 * Not issuing a request whose failure is predictable is cheaper and quieter than
 * handling it gracefully.
 */
export function useUnreadCount(enabled = true) {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: UNREAD_KEY,
    retry: false,
    queryFn: () => api.get<{ unreadCount: number }>("/v1/notifications/unread-count"),
  });
}
